/**
 * Closed prop catalog and island waterline-skirt helpers. Maps store prop ids (`"palm-a"`);
 * this module is the only place those become texture keys.
 *
 * Another pass, still not finished. Three palms and a lighthouse, now Y-sorted
 * with poles. Stamps only — a long way from looking alive. More props come next;
 * do not treat `sizeM` or these SVGs as finished art.
 */

import { PIXELS_PER_METER } from '../constants.ts';
import { clamp } from '../math.ts';
import { resolveSwatch } from './palette.ts';
import type { Island, IslandDecoration, PropId } from './types.ts';

export const PROP_IDS = ['palm-a', 'palm-b', 'palm-c', 'lighthouse'] as const;
export const PROP_CATEGORIES = ['vegetation', 'structure'] as const;
export type PropCategory = (typeof PROP_CATEGORIES)[number];

export const PROP_CATEGORY_LABELS: Record<PropCategory, string> = {
  vegetation: 'Vegetation',
  structure: 'Structures',
};

export const MIN_SKIRT_M = 0;
export const MAX_SKIRT_M = 80;
export const DEFAULT_ISLAND_SKIRT_M = 0;
export const DEFAULT_PROP_SCALE = 1;
export const PROP_TEXTURE_SIZE = 256;
/** Images plant on the trunk base, not the sprite centre. */
export const PROP_ORIGIN_X = 0.5;
export const PROP_ORIGIN_Y = 1;
/** World-pixel radius used to click a prop. Scales with the decoration. */
export const PROP_HIT_RADIUS_PX = 48;
export const SCATTER_INSET_PX = 60;
export const SCATTER_MIN_GAP_PX = 90;
export const SCATTER_ATTEMPTS_PER_PROP = 24;
/** Target world-px² per planted prop when scattering. */
export const SCATTER_AREA_PX2 = 20_000;

export type PropDef = {
  id: PropId;
  label: string;
  texture: string;
  category: PropCategory;
  /** Sprite box in metres at scale 1. Ink is shorter than this by viewBox padding. */
  sizeM: number;
};

/**
 * Sprite-box metres at scale 1. Palms land in the 12–18 m ink band next to the
 * 7 m hull; the lighthouse is a short coastal light (~37 m of ink), not a palm.
 */
export const PROPS: readonly PropDef[] = [
  { id: 'palm-a', label: 'Palm A', texture: 'palm-a', category: 'vegetation', sizeM: 22 },
  { id: 'palm-b', label: 'Palm B', texture: 'palm-b', category: 'vegetation', sizeM: 23 },
  { id: 'palm-c', label: 'Palm C', texture: 'palm-c', category: 'vegetation', sizeM: 22 },
  { id: 'lighthouse', label: 'Lighthouse', texture: 'lighthouse', category: 'structure', sizeM: 40 },
];

export function isPropId(value: unknown): value is PropId {
  return typeof value === 'string' && (PROP_IDS as readonly string[]).includes(value);
}

export function propDef(id: PropId): PropDef {
  return PROPS.find((entry) => entry.id === id) ?? PROPS[0]!;
}

export function propsInCategory(category: PropCategory): readonly PropDef[] {
  return PROPS.filter((entry) => entry.category === category);
}

export function clampSkirtM(value: number): number {
  return clamp(value, MIN_SKIRT_M, MAX_SKIRT_M);
}

export function emptyIslandLook(): Pick<Island, 'skirtM' | 'decorations' | 'nextDecorationId'> {
  return {
    skirtM: DEFAULT_ISLAND_SKIRT_M,
    decorations: [],
    nextDecorationId: 1,
  };
}

export function cloneDecoration(decoration: IslandDecoration): IslandDecoration {
  return {
    id: decoration.id,
    catalog: decoration.catalog,
    x: decoration.x,
    y: decoration.y,
    scale: decoration.scale,
    rotation: decoration.rotation,
  };
}

export function cloneIslandLook(
  island: Pick<Island, 'skirtM' | 'decorations' | 'nextDecorationId'>,
): Pick<Island, 'skirtM' | 'decorations' | 'nextDecorationId'> {
  return {
    skirtM: island.skirtM,
    decorations: island.decorations.map(cloneDecoration),
    nextDecorationId: island.nextDecorationId,
  };
}

export type IslandAppearance = {
  cliff: number;
  skirt: number;
  skirtM: number;
};

export function islandAppearance(island: Island): IslandAppearance {
  return {
    cliff: 0,
    skirt: resolveSwatch('dune').fill,
    skirtM: island.skirtM,
  };
}

export function propTextureSize(catalog: PropId): number {
  return Math.max(PROP_TEXTURE_SIZE, Math.round(propDef(catalog).sizeM * PIXELS_PER_METER));
}

export function propDisplaySize(catalog: PropId, scale: number): number {
  return propDef(catalog).sizeM * PIXELS_PER_METER * Math.max(0.25, scale);
}

export function propHitRadius(decoration: IslandDecoration): number {
  return propDisplaySize(decoration.catalog, decoration.scale) * 0.2;
}

/** Image (x, y) is the trunk base. The click box is the sprite standing on that point. */
export function propHitsPoint(
  base: { x: number; y: number },
  catalog: PropId,
  scale: number,
  point: { x: number; y: number },
  extraRadius: number,
): boolean {
  const size = propDisplaySize(catalog, scale);
  const pad = extraRadius;

  return (
    point.x >= base.x - size / 2 - pad &&
    point.x <= base.x + size / 2 + pad &&
    point.y >= base.y - size - pad &&
    point.y <= base.y + pad
  );
}

export function skirtWidthPx(skirtM: number): number {
  return Math.max(0, skirtM) * PIXELS_PER_METER * 2;
}
